import { useLazyQuery } from '@apollo/client';
import {
  getTranslatedText,
  Loader,
  NoDataCard,
  HeaderTag,
  withErrorBoundary,
  AccessControl
} from '@nile/components';
import { RMMClient } from '@nile/graphql';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  Popover,
  Button,
  Sheet,
  Modal,
  Page,
  TextField
} from '@superops/ui-design-system';
import {
  GET_ASSOCIATED_ASSET_CLASS_LIST_FOR_ASSET,
  GET_NON_MONITORED_ASSET_CLASS_LIST
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import AssetAssociationSheet from '@nile/rmm/src/modules/it-documentation/components/sub-pages/asset-association-sheet/index';
import {
  NonMonitoredAssetListBlock,
  SearchBarForAssetList
} from '@nile/rmm/src/modules/it-documentation/components/sub-pages/assets-association-container/styles';
import AssociatedAssetClass from '@nile/rmm/src/modules/it-documentation/components/sub-pages/associated-asset-class';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import envConfig from '@nile/home/src/Config/env';
import { SVGIcon } from '@superops/super-icons';
import AssociateMonitorAsset from '@nile/rmm/src/modules/it-documentation/components/sub-pages/monitored-asset-list/associate-monitor-asset';
import { AssetAttachmentContainer } from '@nile/rmm/src/modules/asset-management/components/sub-pages/asset-attachments';
import ShareItDocumentToClients from '@nile/rmm/src/modules/it-documentation/components/sub-pages/asset-share/share-it-document';

const defaultVarsForAssociatedAssets = (client, site) => {
  let variables = { condition: null };

  if (client) {
    variables.condition = {
      joinOperator: 'OR',
      operands: [
        {
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'client.accountId',
              operator: 'is',
              value: client
            }
          ]
        },
        {
          attribute: 'client.accountId',
          operator: 'isEmpty'
        }
      ]
    };
  }

  if (site) {
    const conditionOperands = {
      joinOperator: 'OR',
      operands: [
        {
          attribute: 'site.id',
          operator: 'is',
          value: site
        },
        {
          attribute: 'site.id',
          operator: 'isEmpty'
        }
      ]
    };
    if (variables?.condition?.operands[0]) {
      variables.condition.operands[0].operands.push(conditionOperands);
    } else {
      variables.condition = conditionOperands;
    }
  }

  if (client && !site) {
    variables.condition.operands[0].operands.push({
      attribute: 'site.id',
      operator: 'isEmpty'
    });
  }
  return variables;
};

function AssetsAssociationContainer(props) {
  const { assetId } = props;

  const flags = useFlags();

  const [nonMonitoredAssetList, setNonMonitoredAssetList] = useState([]);

  const [associateAssetPopover, setAssociateAssetPopover] = useState(false);

  const [sheetActive, setSheetActive] = useState(false);

  const [selectedNMAsset, setSelectedNMAsset] = useState(null);

  const [associatedSheetActive, setAssociatedSheetActive] = useState(false);

  const [refetchListType, setRefetchListType] = useState(null);
  const [relatedAssetClasses, setRelatedAssetClasses] = useState([]);

  const [searchValue, setSearchValue] = useState('');

  const [fetchNonMonitoredAssetClassList] = useLazyQuery(
    GET_NON_MONITORED_ASSET_CLASS_LIST,
    {
      client: RMMClient,
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        const filteredNMAssetClassList = res?.getNonMonitoredAssetClassList.filter(
          (e) => {
            return e.name !== props.assetClassName;
          }
        );
        setNonMonitoredAssetList(filteredNMAssetClassList || []);
      }
    }
  );

  useEffect(() => {
    fetchNonMonitoredAssetClassList({
      variables: {
        ...defaultVarsForAssociatedAssets(props.clientId, props.siteId)
      }
    });
  }, [props.clientId, props.siteId]);

  const [getAssetList, { loading, data }] = useLazyQuery(
    GET_ASSOCIATED_ASSET_CLASS_LIST_FOR_ASSET,
    {
      client: RMMClient,
      fetchPolicy: 'network-only',
      variables: {
        assetId
      },
      onCompleted: (res) => {
        if (res && res.getAssociatedAssetClassListForAsset) {
          setRelatedAssetClasses(res.getAssociatedAssetClassListForAsset);
        }
      }
    }
  );
  useEffect(() => {
    getAssetList();
  }, []);
  const toggleAssociateAssetPopoverActive = () => {
    setAssociateAssetPopover((prev) => !prev);
    if (!associateAssetPopover && !isEmpty(searchValue)) {
      setSearchValue('');
    }
  };

  const toggleSheetActive = useCallback(() => {
    setSheetActive((prev) => !prev);
  }, []);

  const toggleAssociateAsset = useCallback(() => {
    setAssociatedSheetActive((prev) => !prev);
  }, []);

  const associateAssetActivator = (
    <AccessControl
      roleToCheck="itDocumentation.edit"
      render={
        <Button
          secondary
          id={'itAssociateAssetClass'}
          icon={<SVGIcon name="Add" size="sm" />}
          onClick={toggleAssociateAssetPopoverActive}
        >
          {getTranslatedText(props.intl, 'asset.details.itDoc.associate')}
        </Button>
      }
    />
  );

  /**
   *
   * @param data
   * Refetching data based on whether a new associated made or
   * added a new one to existing asset.
   */

  const refetchAsset = (data) => {
    toggleSheetActive();
    if (relatedAssetClasses.find((item) => item?.name === data?.name)) {
      setRefetchListType(data?.name);
    } else {
      getAssetList();
    }
  };
  const NonMonitorAssetsList = () => {
    const filteredAssets =
      nonMonitoredAssetList.filter((asset: { name: string }) => {
        const lowerCaseAsset = asset?.name?.toLowerCase();
        return lowerCaseAsset.includes(searchValue.toLowerCase());
      }) || [];

    return (
      <NonMonitoredAssetListBlock>
        {filteredAssets.map((asset: { name: string }) => {
          return (
            <div
              className="tab-name-block flex-row flex-align-center"
              onClick={() => {
                setSelectedNMAsset(asset);
                toggleSheetActive();
                toggleAssociateAssetPopoverActive();
              }}
            >
              <span className="mr-10">
                <SVGIcon name="ItDocumentationIcon" size="sm" />
              </span>
              <div className="tab-name flex-item-balloon">{asset.name}</div>
            </div>
          );
        })}
      </NonMonitoredAssetListBlock>
    );
  };

  const showItDocumentAttachement =
    flags.itDocAttachement && props?.showMonitoredAssets;
  const showShareITDocument =
    flags?.shareItDocsToClients && props?.showMonitoredAssets;
  const showItDocClientSearch =
    nonMonitoredAssetList && nonMonitoredAssetList?.length > 0;

  return (
    <div className="full-height">
      <div className="vh100-192 flex col g20">
        {props.showMonitoredAssets && (
          <AccessControl
            roleToCheck="asset.view"
            render={
              <AssociateMonitorAsset
                assetId={assetId}
                intl={props.intl}
                showMonitoredAssets={props.showMonitoredAssets}
                clientName={props.clientName}
                siteName={props.siteName}
              />
            }
          />
        )}
        {showItDocumentAttachement && (
          <AccessControl
            roleToCheck="fileAttachments.view"
            render={
              <AssetAttachmentContainer intl={props?.intl} assetId={assetId} />
            }
          />
        )}
        <div className="f11 scroll white-bg br10 min-height-320 white-grey-page">
          <Page
            title={
              <HeaderTag
                noMargin
                isNewLayout
                title={getTranslatedText(
                  props.intl,
                  'asset.details.navbar.itdocumentation'
                )}
              />
            }
            variant="primary"
            contentHeight="100px"
            primaryAction={
              <Popover
                activator={associateAssetActivator}
                active={associateAssetPopover}
                onClose={() => {
                  toggleAssociateAssetPopoverActive();
                }}
              >
                <div className="flex col wm-400">
                  {showItDocClientSearch && (
                    <SearchBarForAssetList>
                      <TextField
                        autoComplete="off"
                        variant="outline"
                        size="default"
                        value={searchValue}
                        onChange={(word) => setSearchValue(word)}
                        placeholder={getTranslatedText(
                          props.intl,
                          'asset.details.fileExlporer.searchInput.placeholder'
                        )}
                        prefix={<SVGIcon name="Search" />}
                      />
                    </SearchBarForAssetList>
                  )}
                  <NonMonitorAssetsList />
                </div>
              </Popover>
            }
          >
            <Sheet open={sheetActive} onClose={toggleSheetActive}>
              <AssetAssociationSheet
                refetchAsset={refetchAsset}
                selectedNMAsset={selectedNMAsset}
                toggleSheet={toggleSheetActive}
                defaultFilter={
                  defaultVarsForAssociatedAssets(props.clientId, props.siteId)
                    ?.condition
                }
              />
            </Sheet>
            <div className="fh">
              {!isEmpty(relatedAssetClasses) ? (
                relatedAssetClasses.map((assetClass) => {
                  if (assetClass?.entityName !== 'WorkStation') {
                    return (
                      <AssociatedAssetClass
                        assetId={assetId}
                        refetchListType={refetchListType}
                        setRefetchListType={setRefetchListType}
                        assetClass={assetClass}
                        showAssetListToAssociate={() => {
                          toggleAssociateAssetPopoverActive();
                          setSelectedNMAsset(assetClass);
                          toggleSheetActive();
                        }}
                      />
                    );
                  }
                })
              ) : !loading ? (
                <div className="flex center jcenter full-height">
                  <NoDataCard
                    imageURL={`url(${`${envConfig.imageRoot}svg/not-found.svg`})`}
                    description={
                      <FormattedMessage id="asset.details.itdocumentation.no.associated.assets" />
                    }
                  />
                </div>
              ) : (
                <Loader />
              )}
            </div>
          </Page>
        </div>
        {showShareITDocument && (
          <ShareItDocumentToClients
            intl={props?.intl}
            assetId={assetId}
            clientId={props.clientId}
            siteId={props.siteId}
          />
        )}
      </div>
    </div>
  );
}

export default withErrorBoundary(
  withRouter(injectIntl(AssetsAssociationContainer))
);
